import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, DateTime } from '~/components/Form';
import { t } from '~/helpers';
import {
  storeData,
  updateData,
} from '~/store/modules/transferenciaAreiaSilo/actions';

const FormContent = styled.div`
  .MuiFormHelperText-root.Mui-error {
    margin-top: 3px;
  }
`;

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.transferenciaAreiaSilo
  );

  const initials = useMemo(() => (create ? data : { ...find, ...data }), [
    create,
    data,
    find,
  ]);

  const [idposto, setIdposto] = useState(initials?.idposto || null);
  const [saco, setSaco] = useState(null);
  const [quantidade, setQuantidade] = useState('');
  const [itens, setItens] = useState([]);
  const [estoqueKey, setEstoqueKey] = useState(0);
  const initialsHydratedRef = useRef(null);
  const initialsKey = useMemo(
    () =>
      create
        ? 'create'
        : String(initials?.idtransferenciaareiasilo || 'empty-update'),
    [create, initials]
  );

  const totalPesoItens = useMemo(
    () =>
      itens.reduce(
        (total, item) =>
          total + Number(item.pesototalkgtransferenciaareiasiloitem || 0),
        0
      ),
    [itens]
  );

  const siloParams = useMemo(
    () =>
      idposto
        ? {
            wherefield: ['idposto', 'silotipoareia'],
            valuewherefield: [idposto, 'S'],
            typewherefield: ['EQUALS', 'EQUALS'],
            orderfield: 'nomepostotipoareia',
            ordination: 'asc',
          }
        : {},
    [idposto]
  );

  const sacoParams = useMemo(
    () =>
      idposto
        ? {
            wherefield: ['idposto', 'silotipoareia'],
            valuewherefield: [idposto, 'N'],
            typewherefield: ['EQUALS', 'EQUALS'],
            orderfield: 'descricaotipoareia',
            ordination: 'asc',
          }
        : {},
    [idposto]
  );

  function getDescricaoEstoque(item) {
    if (!item) return '';
    if (item.estoqueAreiaVO) {
      return (
        item.estoqueAreiaVO.descricaotipoareia ||
        item.estoqueAreiaVO.nomepostotipoareia
      );
    }
    return item.descricaotipoareia || item.nomepostotipoareia;
  }

  function handleAddItem() {
    if (!idposto) {
      toast.error('Informe o posto antes dos itens.');
      return;
    }
    if (!saco || !saco.idpostotipoareia) {
      toast.error('Informe a areia em saco.');
      return;
    }

    const qtd = Number(quantidade);
    const pesoUnitario = Number(saco.pesounitariokgtipoareia || 0);

    if (!qtd || qtd <= 0) {
      toast.error('Quantidade invalida.');
      return;
    }
    if (!pesoUnitario || pesoUnitario <= 0) {
      toast.error('Peso unitario invalido.');
      return;
    }
    if (
      itens.some((item) => item.idpostotipoareiasaco === saco.idpostotipoareia)
    ) {
      toast.error('Este tipo de areia ja foi incluido.');
      return;
    }

    setItens([
      ...itens,
      {
        idpostotipoareiasaco: saco.idpostotipoareia,
        descricao: getDescricaoEstoque(saco),
        quantidadesacostransferenciaareiasiloitem: qtd,
        pesototalkgtransferenciaareiasiloitem: qtd * pesoUnitario,
      },
    ]);
    setSaco(null);
    setQuantidade('');
    reference.current.setFieldValue('idpostotipoareiasacoitem', null);
    setEstoqueKey((key) => key + 1);
  }

  function handleRemoveItem(idpostotipoareiasaco) {
    setItens(
      itens.filter((item) => item.idpostotipoareiasaco !== idpostotipoareiasaco)
    );
  }

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idposto: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        idpostotipoareiasilo: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        datahoratransferenciaareiasilo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        idusuariotransferenciaareiasilo: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
      });

      await schema.validate(param, { abortEarly: false });

      if (itens.length === 0) {
        toast.error('Informe ao menos um item de areia.');
        return;
      }

      const itensTransferencia = itens.map((item) => ({
        idtransferenciaareiasiloitem: item.idtransferenciaareiasiloitem,
        idtransferenciaareiasilo: item.idtransferenciaareiasilo,
        idcliente: item.idcliente,
        idposto: item.idposto,
        idpostotipoareiasaco: item.idpostotipoareiasaco,
        quantidadesacostransferenciaareiasiloitem:
          item.quantidadesacostransferenciaareiasiloitem,
        pesototalkgtransferenciaareiasiloitem:
          item.pesototalkgtransferenciaareiasiloitem,
      }));

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        idposto: create ? Number(param.idposto) : initials.idposto,
        idpostotipoareiasilo: Number(param.idpostotipoareiasilo),
        idusuariotransferenciaareiasilo: Number(
          param.idusuariotransferenciaareiasilo
        ),
        pesototalkgtransferenciaareiasilo: totalPesoItens,
        itens: itensTransferencia,
      };

      delete objSend.idpostotipoareiasacoitem;

      if (create) {
        dispatch(storeData(objSend, params));
      } else {
        dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (
      loading ||
      !reference.current ||
      initialsHydratedRef.current === initialsKey
    )
      return;

    initialsHydratedRef.current = initialsKey;
    reference.current.setData(initials);
    setIdposto(initials?.idposto || null);
    setItens(
      (initials?.itens || []).map((item) => ({
        ...item,
        descricao: getDescricaoEstoque(item),
      }))
    );
  }, [loading, initials, initialsKey, reference]);

  return loading ? (
    <div style={{ height: '14rem', width: '60rem' }}>
      <Spinner loading />
    </div>
  ) : (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <FormContent>
        <Grid
          container
          spacing={2}
          style={{ width: '60rem', maxWidth: '100%' }}
        >
          <Grid item xs={12} md={6}>
            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postos"
              unique="idposto"
              title={t('s.menu.configuracao.posto')}
              disabled={!create}
              getValue={(value) => {
                const novoPosto = value ? value.idposto : null;
                const postoAlterado = novoPosto !== idposto;
                setIdposto(novoPosto);
                reference.current.setFieldValue('idposto', novoPosto);
                if (postoAlterado) {
                  reference.current.setFieldValue('idpostotipoareiasilo', null);
                  reference.current.setFieldValue(
                    'idusuariotransferenciaareiasilo',
                    null
                  );
                  reference.current.setFieldValue(
                    'idpostotipoareiasacoitem',
                    null
                  );
                  setItens([]);
                  setSaco(null);
                  setQuantidade('');
                  setEstoqueKey((key) => key + 1);
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              key={`silo-${idposto || 'sem-posto'}`}
              name="idpostotipoareiasilo"
              label="nomepostotipoareia"
              url="estoqueareia"
              unique="idpostotipoareia"
              title={t('s.operacao.transferenciaareiasilo.silo')}
              params={siloParams}
              disabled={!idposto}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTime
              name="datahoratransferenciaareiasilo"
              label={t('s.operacao.transferenciaareiasilo.datahora')}
              time
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              name="idusuariotransferenciaareiasilo"
              label="nomeusuario"
              url="usuarios"
              unique="idusuario"
              title={t('s.geral.executante')}
              disabled={!idposto}
              params={{
                filterusrposto: idposto,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Input
              name="observacaotransferenciaareiasilo"
              label={t('s.geral.observacao')}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 255 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('s.operacao.transferenciaareiasilo.itens')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              key={`saco-${idposto || 'sem-posto'}-${estoqueKey}`}
              name="idpostotipoareiasacoitem"
              label="descricaotipoareia"
              url="estoqueareia"
              unique="idpostotipoareia"
              title={t('s.operacao.transferenciaareiasilo.saco')}
              params={sacoParams}
              disabled={!idposto}
              getValue={(value) => {
                setSaco(value);
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={t('s.geral.sacos')}
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              value={quantidade}
              onChange={(event) => setQuantidade(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              type="button"
              color="primary"
              variant="contained"
              fullWidth
              onClick={handleAddItem}
            >
              {t('s.geral.botaoincluir')}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('s.operacao.transferenciaareiasilo.saco')}
                    </TableCell>
                    <TableCell align="right">{t('s.geral.sacos')}</TableCell>
                    <TableCell align="right">
                      {t('s.geral.pesototal')} Kg
                    </TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((item) => (
                    <TableRow key={item.idpostotipoareiasaco}>
                      <TableCell>{item.descricao}</TableCell>
                      <TableCell align="right">
                        {item.quantidadesacostransferenciaareiasiloitem}
                      </TableCell>
                      <TableCell align="right">
                        {item.pesototalkgtransferenciaareiasiloitem}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveItem(item.idpostotipoareiasaco)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>{t('s.geral.total')}</strong>
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right">
                      <strong>{totalPesoItens}</strong>
                    </TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </FormContent>
    </Form>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
